import { inject, Injectable } from '@angular/core';
import { WebSocketSubject } from 'rxjs/webSocket';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../environments/environment';

const options = {
    maxReconnectAttempts: 10,
    baseDelay: 1000, // 1 second initial delay
    maxDelay: 30000, // 30 seconds max delay
    heartbeatInterval: 30000, // 30s ping
    heartbeatTimeout: 15000, // 5s wait for pong
} as const;

@Injectable({
    providedIn: 'root',
})
export class WebsocketService {
    private socket$!: WebSocketSubject<any>;
    private readonly webSocketURL = environment.wsUrl;
    private socket?: WebSocket;
    private isConnecting = false;
    private isReconnecting = false;
    private isManuallyClosed = false;
    private reconnectTimeoutId?: number;
    private heartbeatIntervalTimer?: number;
    private heartbeatTimeoutTimer?: number;
    private messageStream = new Subject<any>();
    private socketConnectionClosedSubject: Subject<string> = new Subject<string>();
    private isStreamingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public isStreaming$: Observable<boolean> = this.isStreamingSubject.asObservable();
    private reconnectAttempts = 0;
    private readonly _authService = inject(AuthService);

    public message$: Observable<any> = this.messageStream.asObservable();

    constructor() {
        this.connect();
    }

    private connect(): void {
        if (this.isConnecting) {
            return;
        }

        if (this.socket?.readyState === WebSocket.OPEN) {
            return;
        }

        this.isConnecting = true;
        this.isManuallyClosed = false;

        this._authService.getToken().subscribe({
            next: (token: string) => {
                this.createSocket(token);
            },
            error: (err) => {
                this.isConnecting = false;
                console.log('Failed to get auth token', err);
                this.scheduleReconnect();
            },
        });
    }

    public get connectionClosed$() {
        return this.socketConnectionClosedSubject.asObservable();
    }

    public createSocket(token: string): void {
        this.socket = new WebSocket(
            `${this.webSocketURL}?access_token=${encodeURIComponent(token)}`,
        );
        this.socket.onopen = () => {
            this.isConnecting = false;
            this.reconnectAttempts = 0;
            console.log('WebSocket connection established:', this.socket?.url);
            this.startHeartbeat();
        };

        this.socket.onmessage = (event: MessageEvent) => {
            try {
                const message: any = JSON.parse(event.data);
                if (message) {
                    if (message.type && (message.type as string).toUpperCase() === 'PONG') {
                        this.resetHeartbeatTimeout();
                    }

                    this.messageStream.next(message);
                }
            } catch (err) {
                console.error('Error parsing WS message');
            }
        };
        this.socket.onerror = (error) => {};
        this.socket.onclose = () => {
            this.cleanupSocket();
            if (this.isManuallyClosed) {
                console.log('Websocket is closed manually');
                return;
            }
            console.log('Websocket is closed');
            this.scheduleReconnect();
        };
    }

    public sendMessage(payload: any): void {
        if (this.socket?.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(payload));
        } else {
            console.log('WebSocket is not open, message dropped');
            this.connect();
            setTimeout(() => {
                if (this.socket?.readyState === WebSocket.OPEN) {
                    this.socket.send(JSON.stringify(payload));
                }
            }, 2000);
        }
    }

    public disconnet(): void {
        this.isManuallyClosed = true;
        this.cleanupSocket();
        this.socket?.close(1000);
    }

    private scheduleReconnect(): void {
        if (this.isReconnecting) {
            return;
        }

        if (this.reconnectAttempts >= options.maxReconnectAttempts) {
            console.log('Max reconnect attampts reached');
            return;
        }

        this.isReconnecting = true;
        this.reconnectAttempts++;
        const delay = Math.min(
            options.baseDelay * Math.pow(2, this.reconnectAttempts),
            options.maxDelay,
        );
        console.log(`Reconnecting in ${delay / 1000}s...`);
        this.reconnectTimeoutId = window.setTimeout(() => {
            this.isReconnecting = false;
            this.connect();
        }, delay);
    }

    private startHeartbeat(): void {
        this.stopHeartbeat();
        this.heartbeatIntervalTimer = setInterval(() => {
            const socket = this.socket;
            if (socket && socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify({ type: 'ping' })); // Expects a string literal "pong" back from server

                // Set safety net timer: drop connection if server stops responding
                this.heartbeatTimeoutTimer = setTimeout(() => {
                    console.warn('Heartbeat timeout. Terminating dead connection.');
                    socket.close(); // Triggers onclose event to start reconnect logic
                }, options.heartbeatTimeout);
            }
        }, options.heartbeatInterval);
    }

    private stopHeartbeat(): void {
        if (!this.heartbeatIntervalTimer) {
            clearInterval(this.heartbeatIntervalTimer);
            this.heartbeatIntervalTimer = undefined;
        }
    }

    public cleanupSocket(): void {
        this.stopHeartbeat();
        if (this.reconnectTimeoutId) {
            clearTimeout(this.reconnectTimeoutId);
            this.reconnectTimeoutId = undefined;
        }
        this.socket = undefined;
        this.isConnecting = false;
    }

    private resetHeartbeatTimeout() {
        if (this.heartbeatTimeoutTimer !== undefined) {
            clearTimeout(this.heartbeatTimeoutTimer);
        }
    }
}
